/* global google */
// The line above fixes the "google is not defined" ESLint error
import { Box, Modal, ModalProps, Stack } from "@mui/material";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useState } from "react";
import { DisabledTextField } from "../Forms/DisabledTextField";
import { GMapsLatLng } from "./GMapsLatLng";

const MODAL_DIMENSION = 800;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: MODAL_DIMENSION,
  height: MODAL_DIMENSION,
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

// Coordinates of Ford plant in Cologne
const defaultCenter = {
  lat: 51.005,
  lng: 6.956,
};

const gMapsLatLngOrDefault = (latitude?: number, longitude?: number) => ({
  lat: latitude || defaultCenter.lat,
  lng: longitude || defaultCenter.lng,
});

type Props = {
  setOpen: (open: boolean) => void;
  coordinates: Partial<GMapsLatLng> | undefined;
  updateCoordinates?: (coordinates: GMapsLatLng) => void;
  isMarkerDraggable: boolean;
};

export function GoogleMapsModal({
  open,
  setOpen,
  coordinates,
  updateCoordinates,
  onClose,
  isMarkerDraggable,
  ...otherProps
}: Props & Omit<ModalProps, "children">) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GMAPS_API_KEY ?? "",
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [center, setCenter] = useState(defaultCenter);
  const [selectedCoordinates, setSelectedCoordinates] = useState<GMapsLatLng>(defaultCenter);

  const onLoad = (loadedMap: google.maps.Map) => {
    setMap(loadedMap);
    const initialCoordinates = gMapsLatLngOrDefault(coordinates?.lat, coordinates?.lng);
    setCenter(initialCoordinates);
    setSelectedCoordinates(initialCoordinates);
    if (updateCoordinates) updateCoordinates(initialCoordinates);
  };

  const onMapDragEnd = () => {
    const centerAfterDrag = map?.getCenter();
    setCenter(gMapsLatLngOrDefault(centerAfterDrag?.lat(), centerAfterDrag?.lng()));
  };

  const onMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    const { latLng } = event;
    const gMapsCoordinates = gMapsLatLngOrDefault(latLng?.lat(), latLng?.lng());
    setSelectedCoordinates(gMapsCoordinates);
    if (updateCoordinates) updateCoordinates(gMapsCoordinates);
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
        if (onClose) onClose(event, reason);
      }}
      {...otherProps}
    >
      <Box sx={modalStyle}>
        <Stack direction="row" spacing={2} marginBottom={2}>
          <DisabledTextField value={selectedCoordinates?.lat} label="Latitude" />
          <DisabledTextField value={selectedCoordinates?.lng} label="Longitude" />
        </Stack>
        {isLoaded && coordinates ? (
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "93%",
            }}
            center={center}
            zoom={18}
            onLoad={onLoad}
            onDragEnd={onMapDragEnd}
          >
            <Marker
              position={selectedCoordinates}
              draggable={isMarkerDraggable}
              onDragEnd={onMarkerDragEnd}
            />
          </GoogleMap>
        ) : (
          <>Loading...</>
        )}
      </Box>
    </Modal>
  );
}
